import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";

// context
import context from "../../store/context";

// assets
import Logo from "../../assets/logowhite.svg";
import KachaLogo from "../../assets/kacha.png";
import LoginPattern from "../../assets/login-pattern.svg";

// icons
import CheckIcon from "../icons/Check";
import CloseIcon from "../icons/Close";

// ui
import AlertModal from "./AlertModal";

const FEATURES = [
  "Unlimited Tenders",
  "Email Alerts",
  "SMS Alerts",
  "Telegram Alerts",
  "Save Tenders",
  "4 Languages",
  "Nationwide Coverage",
  "24/7 Support",
];

const PLANS = [
  { id: "3", time: "1 Year", price: "2,360", perMonth: "197", badge: "Best Value" },
  { id: "2", time: "6 Months", price: "1,480", perMonth: "247" },
  { id: "1", time: "3 Months", price: "980", perMonth: "327" },
];

const MINIAPP_ENDPOINT = process.env.REACT_APP_MINIAPP_ENDPOINT;

// how often / how long we poll kacha-invoice-status after a push USSD
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 90000;

const Paywall = () => {
  const { setShowPaywall, getUserData } = useContext(context);
  const navigate = useNavigate();

  const [selected, setSelected] = useState("3");
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);

  const pollTimeoutRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  const close = () => setShowPaywall(false);

  // stop the spinner and surface a friendly failure message
  const failPayment = (
    message = "Your payment didn't go through. Please try again."
  ) => {
    setPaying(false);
    setPayError({ title: "Payment Unsuccessful", message });
  };

  // 2. poll our backend until Kacha confirms the USSD push was paid
  const pollInvoiceStatus = (invoiceId) => {
    const userToken = sessionStorage.getItem("token");
    const startedAt = Date.now();

    const poll = () => {
      fetch(`${MINIAPP_ENDPOINT}/api/miniapp/kacha-invoice-status/${invoiceId}`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${userToken}`,
        },
      })
        .then((res) => res.json().catch(() => null))
        .then((json) => {
          console.log("invoice status", json);
          const data = json && json.data;
          if (data && data.status === "paid") {
            getUserData();
            confetti({
              particleCount: 150,
              spread: 90,
              origin: { y: 0.6 },
              zIndex: 9999,
            });
            closeTimeoutRef.current = setTimeout(() => {
              setPaying(false);
              setShowPaywall(false);
              navigate("/tenders");
            }, 1200);
          } else if (data && data.status === "failed") {
            failPayment(data.reason);
          } else if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
            failPayment(
              "We're still waiting for your payment to confirm. Please check back shortly."
            );
          } else {
            pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
          }
        })
        .catch((error) => {
          console.log("error", error);
          if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
            failPayment();
          } else {
            pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
          }
        });
    };

    poll();
  };

  // 1. Pay Now → get a reference from our backend, then hand it to the
  // superapp's native payment bridge, which owns the confirm/PIN dialog
  const subscribe = () => {
    setPaying(true);
    setPayError(null);
    const userToken = sessionStorage.getItem("token");

    fetch(`${MINIAPP_ENDPOINT}/api/miniapp/kacha-pay`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      body: JSON.stringify({ plan_id: selected }),
    })
      .then((res) => res.json().catch(() => null))
      .then((json) => {
        console.log("pay", json);
        const data = json && json.data;
        const traceNumber = data && data.trace_number;
        if (json && json.status && data && data.reference && traceNumber) {
          if (window.KachaSuperApp && window.KachaSuperApp.requestPayment) {
            window.KachaSuperApp.requestPayment({ reference: data.reference });
            const invoiceId = traceNumber.split("-KACHA-")[0];
            pollInvoiceStatus(invoiceId);
          } else {
            failPayment(
              "This payment needs to be completed inside the Kacha SuperApp."
            );
          }
        } else {
          failPayment(json && json.message);
        }
      })
      .catch((error) => {
        console.log("error", error);
        failPayment();
      });
  };

  // stop polling / pending timers if the paywall unmounts mid-flight
  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const selectedPlan = PLANS.find((p) => p.id === selected);

  return (
    <div className="fixed inset-0 z-[150] bg-[#352a66] flex flex-col animate-sheet-fade">
      <div className="flex-1 overflow-y-auto flex flex-col">
        {/* dark indigo hero */}
        <div className="relative shrink-0 text-white px-6 pt-10 pb-7 text-center bg-gradient-to-b from-[#1f1748] to-[#352a66]">
          {/* halftone pattern texture */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `url(${LoginPattern})`,
              backgroundSize: "cover",
              backgroundPosition: "bottom",
              backgroundRepeat: "no-repeat",
              opacity: 0.22,
            }}
          />
          <button
            onClick={close}
            aria-label="Close"
            className="absolute top-5 right-5 z-10 w-9 h-9 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center active:scale-95 transition-transform"
          >
            <CloseIcon styles="w-5 h-5" />
          </button>
          <div className="relative">
            <img src={Logo} alt="iChereta" className="h-8 mx-auto mb-3" />
            <h1 className="text-[24px] font-bold leading-tight">
              Unlock Full Access
            </h1>
            <p className="mt-1 text-white/80 text-[13px]">
              All tenders. All channels. All over Ethiopia.
            </p>
          </div>
        </div>

        {/* content cutout — rounded top curving into the indigo hero */}
        <div className="flex-1 rounded-t-[28px] bg-gray-50 px-5 pt-6 pb-6">
          {/* features */}
          <div className="bg-white rounded-2xl p-4 border border-gray-100">
            <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
              {FEATURES.map((f) => (
                <div key={f} className="flex items-start gap-2">
                  <span className="w-4 h-4 mt-[1px] rounded-full bg-green-500/10 flex items-center justify-center shrink-0">
                    <CheckIcon styles="w-2.5 h-2.5 text-green-600" />
                  </span>
                  <span className="text-[13px] text-gray-700 leading-snug">
                    {f}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* plans */}
          <div className="mt-6">
            <h2 className="px-1 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
              Choose your plan
            </h2>
            <div className="flex flex-col gap-3">
            {PLANS.map((plan) => {
              const active = selected === plan.id;
              return (
                <button
                  key={plan.id}
                  onClick={() => setSelected(plan.id)}
                  className={`w-full flex items-center gap-3 p-4 rounded-2xl border text-left transition-colors ${
                    active
                      ? "border-[#664bc7] ring-1 ring-[#664bc7] bg-[#664bc7]/[0.05]"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <span
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                      active
                        ? "border-[#664bc7] bg-[#664bc7]"
                        : "border-gray-300"
                    }`}
                  >
                    {active && <CheckIcon styles="w-4 h-4 text-white" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">
                        {plan.time}
                      </span>
                      {plan.badge && (
                        <span className="text-[10px] font-bold uppercase tracking-wide bg-green-500 text-white px-2 py-0.5 rounded-full">
                          {plan.badge}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">
                      ≈ {plan.perMonth} birr / month
                    </span>
                  </div>
                  <span className="font-bold text-gray-900 shrink-0">
                    {plan.price}{" "}
                    <span className="text-xs font-normal text-gray-500">
                      birr
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
            <p className="px-1 pt-3 text-[11px] text-gray-400 text-center">
              All prices include 15% VAT.
            </p>
          </div>
        </div>
      </div>

      {/* sticky CTA */}
      <div className="border-t border-gray-200 bg-white/90 backdrop-blur-md px-5 pt-4 pb-[calc(env(safe-area-inset-bottom)+16px)]">
        <button
          onClick={subscribe}
          disabled={paying}
          className="w-full h-[52px] rounded-2xl text-white font-semibold text-[17px] relative overflow-hidden bg-gradient-to-b from-[#7c61e0] to-[#5b40b8] shadow-[0_4px_12px_rgba(102,75,199,0.40),inset_0_1px_0_rgba(255,255,255,0.30),inset_0_-2px_4px_rgba(74,54,149,0.55)] active:shadow-[inset_0_2px_6px_rgba(74,54,149,0.60)] active:translate-y-px transition-all duration-150 disabled:opacity-70"
        >
          {paying ? (
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-6 h-6 border-t-2 border-white rounded-full animate-spin" />
            </span>
          ) : (
            <>
              {/* fixed logo + indigo divider, anchored left so it stays put */}
              <span className="absolute left-6 top-1/2 -translate-y-1/2 flex items-center gap-3">
                <span className="w-9 h-9 rounded-full bg-white flex items-center justify-center shrink-0 shadow-sm">
                  <img
                    src={KachaLogo}
                    alt="Kacha"
                    className="w-6 h-6 object-contain"
                  />
                </span>
                <span className="h-6 w-px bg-[#2a1f58]/60" />
              </span>
              <span className="block text-center">
                Pay Now · {selectedPlan.price} birr
              </span>
            </>
          )}
        </button>
      </div>

      {payError && (
        <AlertModal
          title={payError.title}
          message={payError.message}
          buttonLabel="Try Again"
          onClose={() => setPayError(null)}
        />
      )}
    </div>
  );
};

export default Paywall;
